import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { TimelineEvent } from '../types';
import { AppDialogFrame } from '../../../components/overlays/AppDialogFrame';
import { TailwindDateField } from '../../../components/forms/TailwindDateField';

interface ScheduleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (event: Partial<TimelineEvent>) => boolean | void | Promise<boolean | void>;
    initialData?: TimelineEvent | null;
}

export const ScheduleModal: React.FC<ScheduleModalProps> = ({ isOpen, onClose, onSave, initialData }) => {
    const [title, setTitle] = useState('');
    const [type, setType] = useState('Interview');
    const [date, setDate] = useState('2026-02-08');
    const [time, setTime] = useState('10:00');
    const [duration, setDuration] = useState('30 mins');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setTitle(initialData.title === 'Event Scheduled' ? '' : initialData.title);
            setType(initialData.eventType || 'Interview');
            setDuration(initialData.duration || '30 mins');
            if (initialData.date === 'Today') setDate(new Date().toISOString().split('T')[0]);
            setTime(initialData.time.split(' ')[0]);
        }
    }, [initialData, isOpen]);

    if (!isOpen) return null;

    const handleSave = async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            const result = await onSave({
                id: initialData?.id || Math.random().toString(36).substr(2, 9),
                title: title || (type + ' Scheduled'),
                eventType: type,
                date: date === new Date().toISOString().split('T')[0] ? 'Today' : date,
                time: time + ' AM',
                duration: duration,
                type: 'event',
                color: 'primary',
                description: `${type} scheduled for ${title || 'Applicant'}. Duration: ${duration}.`
            });
            if (result !== false) {
                onClose();
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <AppDialogFrame
            open={isOpen}
            onClose={onClose}
            title={initialData ? 'Modify Event' : 'Schedule Event'}
            description="Create or update admissions scheduling entries."
            testId="admissions-schedule-modal"
            maxWidthClassName="max-w-md"
            titleClassName="text-lg"
            bodyClassName="space-y-5"
            bodyPaddingClassName="p-6"
            footerPaddingClassName="px-6 py-5"
            footerClassName="justify-between gap-4"
            footer={(
                <>
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 rounded-xl border border-gray-200 py-3 text-sm font-bold text-irongrey transition-colors hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => void handleSave()}
                        disabled={isSaving}
                        data-testid="admissions-schedule-save"
                        className="flex-1 rounded-xl bg-irongrey py-3 text-sm font-bold text-white shadow-lg shadow-irongrey/20 transition-all hover:bg-irongrey/90 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isSaving ? 'Saving...' : 'Save Event'}
                    </button>
                </>
            )}
        >
            <section>
                <label className="mb-1.5 block text-xs font-bold text-irongrey/60">Event Title</label>
                <input 
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Interview: Yael Gertler"
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-irongrey placeholder:text-irongrey/30 focus:border-primary focus:ring-primary"
                />
            </section>

            <section>
                <label className="mb-1.5 block text-xs font-bold text-irongrey/60">Event Type</label>
                <select 
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-irongrey focus:border-primary focus:ring-primary"
                >
                    <option value="School visit">School visit</option>
                    <option value="Interview">Interview</option>
                    <option value="Other">Other</option>
                </select>
            </section>

            <section className="grid grid-cols-2 gap-4">
                <section>
                    <label className="mb-1.5 block text-xs font-bold text-irongrey/60">Date</label>
                    <TailwindDateField
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-irongrey focus:border-primary focus:ring-primary"
                    />
                </section>
                <section>
                    <label className="mb-1.5 block text-xs font-bold text-irongrey/60">Time</label>
                    <div className="relative">
                        <input 
                            type="time"
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                            className="w-full rounded-xl border border-gray-200 bg-white pl-4 pr-10 py-2.5 text-sm font-medium text-irongrey focus:border-primary focus:ring-primary"
                        />
                        <Clock className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-irongrey/30" size={16} />
                    </div>
                </section>
            </section>

            <section>
                <label className="mb-1.5 block text-xs font-bold text-irongrey/60">Duration</label>
                <select 
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-irongrey focus:border-primary focus:ring-primary"
                >
                    <option value="15 mins">15 mins</option>
                    <option value="30 mins">30 mins</option>
                    <option value="45 mins">45 mins</option>
                    <option value="1 hr">1 hr</option>
                    <option value="1.5 hrs">1.5 hrs</option>
                </select>
            </section>
        </AppDialogFrame>
    );
};
